import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import {
  dismissInstallHint,
  isIosDevice,
  isStandaloneDisplay,
  hardReloadApp,
  unregisterDevServiceWorkers,
  wasInstallDismissed
} from './detection';
import { PwaContext } from './pwaContext';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export function PwaProvider({ children }) {
  const isOnline = useOnlineStatus();
  const [isStandalone, setIsStandalone] = useState(() => isStandaloneDisplay());
  const [isIos, setIsIos] = useState(() => isIosDevice());
  const [canInstall, setCanInstall] = useState(false);
  const [needReload, setNeedReload] = useState(false);
  const [installHintVisible, setInstallHintVisible] = useState(false);
  const [iosHelpVisible, setIosHelpVisible] = useState(false);
  const deferredPromptRef = useRef(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    setIsIos(isIosDevice());
    setIsStandalone(isStandaloneDisplay());

    const media = window.matchMedia('(display-mode: standalone)');
    const handleDisplayMode = () => setIsStandalone(isStandaloneDisplay());

    if (media.addEventListener) {
      media.addEventListener('change', handleDisplayMode);
    } else {
      media.addListener(handleDisplayMode);
    }

    return () => {
      if (media.removeEventListener) {
        media.removeEventListener('change', handleDisplayMode);
      } else {
        media.removeListener(handleDisplayMode);
      }
    };
  }, []);

  useEffect(() => {
    if (isStandalone || wasInstallDismissed()) return undefined;

    const timeoutId = window.setTimeout(() => {
      if (!isStandaloneDisplay() && !wasInstallDismissed()) {
        setInstallHintVisible(true);
      }
    }, 1200);

    return () => window.clearTimeout(timeoutId);
  }, [isStandalone]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      setCanInstall(true);
    };

    const handleAppInstalled = () => {
      deferredPromptRef.current = null;
      setCanInstall(false);
      setInstallHintVisible(false);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (import.meta.env.DEV) {
      unregisterDevServiceWorkers().catch(() => {});
      return undefined;
    }

    let cancelled = false;
    let intervalId;
    let onVisibilityChange;

    import('virtual:pwa-register').then(({ registerSW }) => {
      if (cancelled) return;

      registerSW({
        immediate: true,
        onNeedReload() {
          setNeedReload(true);
        },
        onRegisteredSW(swUrl, registration) {
          if (!registration || !swUrl) return;

          const checkForUpdate = () => {
            if (document.visibilityState === 'visible') {
              registration.update().catch(() => {});
            }
          };

          intervalId = window.setInterval(checkForUpdate, UPDATE_CHECK_INTERVAL_MS);
          onVisibilityChange = checkForUpdate;
          document.addEventListener('visibilitychange', onVisibilityChange);
        }
      });
    });

    return () => {
      cancelled = true;
      if (intervalId) window.clearInterval(intervalId);
      if (onVisibilityChange) {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (isStandalone) return 'accepted';

    const deferredPrompt = deferredPromptRef.current;
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      deferredPromptRef.current = null;
      setCanInstall(false);
      if (choice.outcome === 'accepted') {
        setInstallHintVisible(false);
      }
      return choice.outcome;
    }

    if (isIos) {
      setIosHelpVisible(true);
      setInstallHintVisible(true);
      return 'ios-instructions';
    }

    return 'unavailable';
  }, [isIos, isStandalone]);

  const dismissInstall = useCallback(() => {
    dismissInstallHint();
    setInstallHintVisible(false);
    setIosHelpVisible(false);
  }, []);

  const reloadApp = useCallback(() => {
    if (reloadingRef.current) return;
    reloadingRef.current = true;
    hardReloadApp().catch(() => {
      window.location.reload();
    });
  }, []);

  const value = useMemo(
    () => ({
      isOnline,
      isStandalone,
      isIos,
      canInstall,
      needReload,
      installHintVisible: installHintVisible && !isStandalone && !wasInstallDismissed(),
      iosHelpVisible,
      promptInstall,
      dismissInstall,
      reloadApp
    }),
    [
      canInstall,
      dismissInstall,
      installHintVisible,
      iosHelpVisible,
      isIos,
      isOnline,
      isStandalone,
      needReload,
      promptInstall,
      reloadApp
    ]
  );

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>;
}
